import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Sparkles, ArrowRight, Check, Wand2, Gem, Plane, Layers,
  Facebook, Instagram, Linkedin, Twitter, Users, BookOpen, CalendarDays,
  BarChart3, Zap, Mail, Search, Megaphone, MessageSquare, Video, ListChecks,
  Clock, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { COMING_SOON_BY_PLAN, COMING_SOON_FEATURES } from "@/lib/comingSoon";
import {
  EyebrowTag, SectionBackdrop, GlowPanel, StatRing, Sparkline,
  PlatformBadge, PostPreviewFrame, TacoTruckCreative, WeekGrid, ProgressRail,
} from "@/components/premium";

// ---------------------------------------------------------------------
// Homepage — Postmoro (Phase 2 premium polish)
// ---------------------------------------------------------------------
// New section order (product-led, not text-led):
//   1. Hero — left copy + right composer/preview mockup (real UI shape)
//   2. Content pillars — 3 richer 60/40 panels demonstrating the flow
//   3. Multi-platform publishing — channel-native tiles + WeekGrid
//   4. Multi-Business workspaces — visual switcher demonstration
//   5. Analytics teaser — sparklines + StatRing (clearly labeled demo)
//   6. Pricing — 4 tiers, Pro featured; unchanged values
//   7. Final CTA — full-bleed teal-glow band
//   8. Footer — preserved from P1 SEO work
// SEO copy (title / description / H1 hero-title) preserved verbatim
// from P0/P1 approved strings.
// ---------------------------------------------------------------------

const FALLBACK_TIERS = [
  { id: "free", name: "Free Trial", price: 0, period: "month", highlight: false,
    features: ["10 AI generations / month", "5 published posts / month", "All 4 social platforms"], cta: "Start free" },
  { id: "starter", name: "Starter", price: 19.99, period: "month", highlight: false,
    features: ["50 AI generations", "25 published posts", "All 4 social platforms"], cta: "Choose Starter" },
  { id: "pro", name: "Pro", price: 79.99, period: "month", highlight: true,
    features: ["200 AI generations", "60 published posts", "All social platforms", "Advanced AI tone control"], cta: "Choose Pro" },
  { id: "business", name: "Business", price: 249.99, period: "month", highlight: false,
    features: ["1,000 AI generations", "Unlimited published posts (fair-use)", "All social platforms", "Teams — 3 team seats included", "Multi-platform Autopilot batching", "Custom brand voice"], cta: "Choose Business" },
];

const PLAN_HIGHLIGHTS = {
  free:     ["10 AI generations / month", "5 published posts / month", "All 4 social platforms"],
  starter:  ["50 AI generations / month", "25 published posts / month", "All 4 social platforms"],
  pro:      ["200 AI generations / month", "60 published posts / month", "All 4 platforms", "Advanced AI tone control"],
  business: ["1,000 AI generations / month", "Unlimited published posts (fair-use)", "All 4 platforms", "Teams — 3 team seats included", "Custom brand voice + Autopilot batching"],
};

function fmtPrice(p) {
  if (p == null) return "0";
  const num = Number(p);
  if (Number.isNaN(num)) return String(p);
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

export default function LandingPage() {
  const [tiers, setTiers] = useState(FALLBACK_TIERS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/billing/plans");
        const remote = res?.data?.tiers;
        if (!cancelled && Array.isArray(remote) && remote.length) {
          setTiers(remote.map((t) => ({ ...t, cta: t.id === "free" ? "Start free" : `Choose ${t.name}` })));
        }
      } catch (_e) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)] overflow-x-hidden">
      <SEO
        title="Postmoro | AI Social Media Management & Marketing Automation"
        description="Postmoro is an AI social media management platform for businesses. Create content, schedule posts, automate social media, and manage your marketing from one workspace."
        path="/"
      />

      {/* -------------------- Header -------------------- */}
      <header className="sticky top-0 z-30 bg-[color:rgba(11,13,16,0.72)] backdrop-blur border-b border-[color:var(--border-subtle)]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="landing-logo">
            <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center shadow-[0_4px_18px_rgba(27,168,159,0.45)] shrink-0">
              <Gem className="h-4 w-4" />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight truncate">Postmoro</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-[color:var(--text-2)]">
            <Link to="/features" className="hover:text-[color:var(--text-1)]" data-testid="landing-nav-features">Features</Link>
            <Link to="/pricing" className="hover:text-[color:var(--text-1)]" data-testid="landing-nav-pricing">Pricing</Link>
            <Link to="/about" className="hover:text-[color:var(--text-1)]" data-testid="landing-nav-about">About</Link>
            <Link to="/contact" className="hover:text-[color:var(--text-1)]" data-testid="landing-nav-contact">Contact</Link>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/login" className="hidden sm:inline-flex"><Button variant="ghost" className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)]" data-testid="landing-signin">Sign in</Button></Link>
            <Link to="/login"><Button className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" data-testid="landing-cta-header">Start free</Button></Link>
          </div>
        </div>
      </header>

      {/* -------------------- 1. Hero -------------------- */}
      <section className="relative pt-14 pb-20 sm:pt-20 sm:pb-28" data-testid="landing-hero">
        <SectionBackdrop variant="grid" />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center">
          <div>
            <EyebrowTag>
              <Sparkles className="h-3 w-3" /> AI social media management
            </EyebrowTag>
            <h1 className="font-display mt-6 text-[36px] sm:text-5xl lg:text-[60px] font-semibold tracking-[-0.02em] leading-[1.05]" data-testid="hero-title">
              Postmoro Is Your AI Social Media Assistant
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-xl leading-relaxed">
              Postmoro helps you create content, publish across social platforms, automate your marketing workflow, and surface useful growth ideas — all from one calm workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold px-6" data-testid="landing-cta-hero-start">
                  Get Started Free <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-[color:var(--border-subtle)] text-[color:var(--text-1)] px-6" data-testid="landing-cta-hero-pricing">
                  See Pricing
                </Button>
              </Link>
            </div>
            <p className="mt-3 text-xs text-[color:var(--text-3)]">No credit card required. Cancel any time.</p>
            <div className="mt-8 flex items-center gap-5 text-xs text-[color:var(--text-3)]">
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> One workspace</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> 4 platforms</span>
              <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> Autopilot</span>
            </div>
          </div>

          {/* Right: composer + preview mock */}
          <div className="relative" aria-hidden="true">
            <style>{`
              @keyframes pmGlowPulse { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: .85; transform: scale(1.05); } }
              @media (prefers-reduced-motion: reduce) { .pm-glow-pulse { animation: none !important; } }
              .pm-glow-pulse { animation: pmGlowPulse 6s ease-in-out infinite; }
            `}</style>
            <div className="pm-glow-pulse absolute -inset-8 rounded-[36px]"
                 style={{ background: "radial-gradient(ellipse at 60% 45%, rgba(27,168,159,0.32), rgba(47,143,229,0.14) 40%, rgba(27,168,159,0) 70%)", filter: "blur(38px)" }} />
            <div aria-hidden="true" className="absolute -right-2 -top-12 hidden lg:block">
              <GlowPanel variant="raised" className="px-3 py-2 flex items-center gap-2 shadow-[0_20px_40px_-24px_rgba(0,0,0,0.7)]">
                <span className="h-2 w-2 rounded-full bg-[color:var(--brand-primary)] shadow-[0_0_10px_rgba(27,168,159,0.85)]" />
                <span className="text-[11px] text-[color:var(--text-2)]">Autopilot · queued for Thu 08:30</span>
              </GlowPanel>
            </div>
            <GlowPanel variant="featured" className="relative p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <EyebrowTag><Wand2 className="h-3 w-3" /> Generate Post · demo</EyebrowTag>
                <div className="hidden sm:flex items-center gap-1.5">
                  <PlatformBadge variant="postmoro" platform="instagram" size="sm" />
                </div>
              </div>
              <div className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Topic / offer</div>
                <div className="mt-1 text-sm text-[color:var(--text-1)] leading-relaxed">
                  Taco Tuesday — fresh street tacos and late-night service this week.
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Goal</div>
                  <div className="text-[color:var(--text-1)] mt-0.5">Promote</div>
                </div>
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Tone</div>
                  <div className="text-[color:var(--text-1)] mt-0.5">Bold</div>
                </div>
                <div className="rounded-md border border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.12)] px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Length</div>
                  <div className="text-[color:var(--text-1)] mt-0.5">Standard</div>
                </div>
              </div>
              <div className="mt-4">
                <PostPreviewFrame
                  platform="instagram"
                  handle="Cactus Comet Tacos · Demo preview"
                  caption={"Taco Tuesday is on.\n\nFresh street tacos, bold flavors, and late-night service are rolling all evening. Grab your crew, find the truck, and come hungry."}
                  imageColor="linear-gradient(135deg,#1BA89F 0%,#22D3EE 100%)"
                  creative={<TacoTruckCreative />}
                />
              </div>
            </GlowPanel>
            <div className="absolute -bottom-6 -left-4 hidden sm:block">
              <GlowPanel variant="raised" className="px-3 py-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Also publishes to</span>
                <div className="flex items-center gap-1">
                  <PlatformBadge variant="postmoro" platform="facebook" size="sm" />
                  <PlatformBadge variant="postmoro" platform="instagram" size="sm" />
                  <PlatformBadge variant="postmoro" platform="x" size="sm" />
                </div>
              </GlowPanel>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------- 2. Content pillars -------------------- */}
      <section className="relative py-16" data-testid="landing-pillars">
        <SectionBackdrop />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <EyebrowTag>The Postmoro flow</EyebrowTag>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
            Draft, Schedule, And Automate — Without Switching Tabs
          </h2>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            {/* Pillar 1 — Generate */}
            <GlowPanel variant="raised" className="p-5 flex flex-col">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                <Wand2 className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">AI Drafts Every Post</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
                Tell Postmoro a topic, choose a goal and tone, pick post length. It writes the caption, hashtags, and a matching image.
              </p>
              <div className="mt-4 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-xs">
                <div className="text-[color:var(--text-3)] mb-1">Sample output · demo</div>
                <div className="text-[color:var(--text-1)] leading-relaxed">"Taco Tuesday is on — fresh street tacos, bold flavors, and late-night service all evening. Grab your crew and come hungry."</div>
              </div>
              <Link to="/features" className="mt-5 inline-flex items-center gap-1 text-sm text-[color:var(--brand-primary)] hover:underline">Explore features <ArrowRight className="h-3.5 w-3.5" /></Link>
            </GlowPanel>

            {/* Pillar 2 — Autopilot */}
            <GlowPanel variant="raised" className="p-5 flex flex-col">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                <Plane className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">Autopilot Fills The Calendar</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
                Pick the days and times you want to post. Autopilot drafts each one and drops it into the approval queue — or publishes automatically when you're ready.
              </p>
              <div className="mt-4">
                <WeekGrid slots={[1,0,2,1,1,0,2]} activeIdx={2} />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Clock className="h-3.5 w-3.5 text-[color:var(--brand-primary)] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] leading-none">Next run</div>
                    <div className="text-[11px] text-[color:var(--text-1)] mt-0.5 truncate">Wed 09:00 · 2 drafts queued</div>
                  </div>
                </div>
                <PlatformBadge variant="postmoro" platform="instagram" size="sm" />
              </div>
              <Link to="/features/autopilot" className="mt-5 inline-flex items-center gap-1 text-sm text-[color:var(--brand-primary)] hover:underline">Explore Autopilot <ArrowRight className="h-3.5 w-3.5" /></Link>
            </GlowPanel>

            {/* Pillar 3 — Brand Learning */}
            <GlowPanel variant="raised" className="p-5 flex flex-col">
              <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                <BookOpen className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">Brand Learning Matches Your Voice</h3>
              <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
                Paste in a handful of posts your business already loves. Postmoro adapts new drafts to match your sentence rhythm, tone, and go-to phrases.
              </p>
              <div className="mt-4 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 text-[11px] space-y-2">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-3)]">Generic draft</div>
                  <div className="mt-1 text-[color:var(--text-2)] line-through decoration-[color:rgba(239,68,68,0.55)] decoration-[1.5px]">
                    "We are delighted to inform our valued customers that our food truck will be offering tacos on Tuesday evening."
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[color:var(--brand-primary)]">Learned voice · demo</div>
                  <div className="mt-1 text-[color:var(--text-1)]">
                    "Taco Tuesday is on. Come hungry."
                  </div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["bold","punchy","street-food","late-night","local"].map((tag) => (
                  <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full border border-[color:rgba(27,168,159,0.3)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--brand-primary)]">{tag}</span>
                ))}
              </div>
              <Link to="/features/brand-learning" className="mt-5 inline-flex items-center gap-1 text-sm text-[color:var(--brand-primary)] hover:underline">Explore Brand Learning <ArrowRight className="h-3.5 w-3.5" /></Link>
            </GlowPanel>
          </div>
        </div>
      </section>

      {/* -------------------- 2A. What Postmoro does -------------------- */}
      <section className="relative py-14" data-testid="landing-what-postmoro-does">
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <EyebrowTag>What Postmoro does</EyebrowTag>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Everything Your Social Media Needs — In One Calm Workspace</h2>
            <p className="mt-3 text-[color:var(--text-2)] leading-relaxed">Postmoro replaces the shuffle between AI writer, scheduler, image tool, and separate platform tabs with a single, quiet place to run your social media.</p>
          </div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Wand2, label: "Create social content", body: "AI drafts every caption + a matching image." },
              { icon: CalendarDays, label: "Schedule & publish", body: "One calendar for Facebook, IG, LinkedIn, X." },
              { icon: Plane, label: "Automate with Autopilot", body: "Weeks of posts drafted and queued for you." },
              { icon: BookOpen, label: "Stay on-brand", body: "Brand Learning matches your existing voice." },
              { icon: Layers, label: "Multi-business ready", body: "Up to 4 workspaces on the Business plan." },
              { icon: BarChart3, label: "Track activity", body: "See what's publishing and what's scheduled." },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3 rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-4">
                <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] shrink-0">
                  <item.icon className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[color:var(--text-1)]">{item.label}</div>
                  <div className="mt-0.5 text-xs text-[color:var(--text-3)] leading-relaxed">{item.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------- 2B. Problems Postmoro solves -------------------- */}
      <section className="relative py-16" data-testid="landing-problems">
        <SectionBackdrop />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <EyebrowTag>Why teams switch to Postmoro</EyebrowTag>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">The Everyday Social-Media Pain, Solved</h2>
            <p className="mt-3 text-[color:var(--text-2)] leading-relaxed">Every row: a real complaint we hear from small businesses, paired with what Postmoro does about it.</p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-3">
            {[
              {
                problem: "I spend too much time on social media.",
                solutionTitle: "Postmoro creates, schedules, and automates the work.",
                solutionBody: "AI drafts every caption + a matching image. Publish to four networks from one calendar. Autopilot handles the recurring cadence for you.",
                icon: Wand2, tag: "Time",
              },
              {
                problem: "I forget to post consistently.",
                solutionTitle: "Autopilot keeps content moving.",
                solutionBody: "Pick your days and times. Postmoro drafts each post, drops it into the approval queue — or publishes automatically once you're ready.",
                icon: Plane, tag: "Consistency",
              },
              {
                problem: "I manage several platforms and tabs.",
                solutionTitle: "One workspace manages them together.",
                solutionBody: "Facebook, Instagram, LinkedIn, and X live in the same calendar with the same brand voice — draft once, adapt per platform, publish everywhere.",
                icon: Layers, tag: "One workspace",
              },
              {
                problem: "My AI content sounds generic.",
                solutionTitle: "Brand Learning + tone controls keep it on-brand.",
                solutionBody: "Paste in your best posts and Postmoro learns your rhythm, phrases, and tone. Every new draft steers toward your voice, not a template.",
                icon: BookOpen, tag: "On-brand",
              },
              {
                problem: "I manage more than one business.",
                solutionTitle: "Multi-Business keeps brands separated.",
                solutionBody: "Business subscribers get up to 4 workspaces — each with its own connected accounts, brand voice, calendar, and Autopilot schedule.",
                icon: Users, tag: "Multi-business",
              },
              {
                problem: "I'm never sure what's going out.",
                solutionTitle: "Every post lands in an approval queue by default.",
                solutionBody: "Review, edit, or reschedule before anything publishes. Switch to fully-automatic publishing once your rhythm feels right.",
                icon: CheckCircle2, tag: "Review",
              },
            ].map((row) => (
              <GlowPanel key={row.problem} variant="raised" className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-[minmax(0,0.9fr)_auto_minmax(0,1.1fr)] gap-3 md:gap-6 items-start">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-3)]">Problem · {row.tag}</div>
                  <div className="mt-2 font-display text-lg font-semibold text-[color:var(--text-1)] leading-snug">"{row.problem}"</div>
                </div>
                <div className="hidden md:flex items-center justify-center pt-6">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center border shrink-0 border-[color:rgba(27,168,159,0.35)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--brand-primary)] inline-flex items-center gap-1.5">
                    <row.icon className="h-3 w-3" /> Postmoro
                  </div>
                  <div className="mt-2 font-display text-lg font-semibold text-[color:var(--text-1)] leading-snug">{row.solutionTitle}</div>
                  <p className="mt-1.5 text-sm text-[color:var(--text-2)] leading-relaxed">{row.solutionBody}</p>
                </div>
              </GlowPanel>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------- 3. Multi-platform publishing -------------------- */}
      <section className="relative py-16" data-testid="landing-publishing">
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 items-center">            <div>
              <EyebrowTag>Publishing</EyebrowTag>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                One Calendar. Four Networks.
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed max-w-xl">
                Facebook Pages, Instagram professional accounts, LinkedIn, and X — all from a single calm workspace. Draft once, adapt the tone per platform, and hand off publishing to Postmoro.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-2 max-w-sm">
                {["facebook","instagram","linkedin","x"].map((p) => (
                  <PlatformBadge variant="postmoro" key={p} platform={p} />
                ))}
              </div>
            </div>
            <GlowPanel variant="raised" className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-[color:var(--text-2)]">
                  <CalendarDays className="h-4 w-4 text-[color:var(--brand-primary)]" />
                  This week · demo
                </div>
                <div className="text-xs text-[color:var(--text-3)]">7-day preview</div>
              </div>
              <WeekGrid slots={[2,1,2,1,3,0,1]} activeIdx={4} />
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2 text-[11px]">
                  <div className="text-[color:var(--text-3)]">Fri 09:00</div>
                  <div className="mt-1 flex items-center gap-1"><PlatformBadge variant="postmoro" platform="linkedin" size="sm" /></div>
                </div>
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2 text-[11px]">
                  <div className="text-[color:var(--text-3)]">Fri 12:00</div>
                  <div className="mt-1 flex items-center gap-1"><PlatformBadge variant="postmoro" platform="instagram" size="sm" /></div>
                </div>
                <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2 text-[11px]">
                  <div className="text-[color:var(--text-3)]">Fri 17:30</div>
                  <div className="mt-1 flex items-center gap-1"><PlatformBadge variant="postmoro" platform="facebook" size="sm" /></div>
                </div>
              </div>
            </GlowPanel>
          </div>
        </div>
      </section>

      {/* -------------------- 4. Multi-Business workspaces -------------------- */}
      <section className="relative py-16" data-testid="landing-multi-business">
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-10 items-center">
            <GlowPanel variant="raised" className="p-5" aria-hidden="true">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">Workspaces · demo</div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-[color:rgba(27,168,159,0.3)] bg-[color:rgba(27,168,159,0.08)] text-[color:var(--brand-primary)]">Business plan</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: "Cactus Comet Tacos", color: "linear-gradient(135deg,#1BA89F,#22D3EE)", active: true },
                  { name: "Wildwood Bakery",    color: "linear-gradient(135deg,#0E7490,#2F8FE5)" },
                  { name: "Loop Fitness",       color: "linear-gradient(135deg,#2F8FE5,#22D3EE)" },
                  { name: "Aster Salon",        color: "linear-gradient(135deg,#1BA89F,#7C6DE0)" },
                ].map((ws) => (
                  <div
                    key={ws.name}
                    className={
                      "rounded-lg border p-3 flex items-center gap-3 " +
                      (ws.active
                        ? "border-[color:rgba(27,168,159,0.55)] bg-[color:rgba(27,168,159,0.08)]"
                        : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]")
                    }
                  >
                    <div className="h-9 w-9 rounded-md flex items-center justify-center text-[color:var(--bg-0)] font-semibold text-sm" style={{ background: ws.color }}>{ws.name[0]}</div>
                    <div className="min-w-0">
                      <div className="text-sm text-[color:var(--text-1)] truncate">{ws.name}</div>
                      <div className="text-[10px] text-[color:var(--text-3)]">3 platforms · Autopilot on</div>
                    </div>
                  </div>
                ))}
              </div>
            </GlowPanel>
            <div>
              <EyebrowTag>Multi-Business</EyebrowTag>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Run Up To Four Businesses From One Postmoro</h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed max-w-xl">
                Business subscribers can switch between up to four separate business workspaces — each with its own brand voice, connected accounts, calendar, and Autopilot schedule. Invite team members with Owner, Admin, or Member roles per workspace.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-[color:var(--text-2)]">
                <li className="inline-flex items-start gap-2"><Check className="h-4 w-4 text-[color:var(--brand-primary)] mt-0.5" /> Up to 4 business workspaces</li>
                <li className="inline-flex items-start gap-2"><Check className="h-4 w-4 text-[color:var(--brand-primary)] mt-0.5" /> Teams — 3 team seats included</li>
                <li className="inline-flex items-start gap-2"><Check className="h-4 w-4 text-[color:var(--brand-primary)] mt-0.5" /> Per-workspace roles &amp; permissions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------- 5. Activity dashboard teaser -------------------- */}
      <section className="relative py-16" data-testid="landing-analytics">
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <EyebrowTag>Activity</EyebrowTag>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">See What's Publishing — And What's Queued Next</h2>
            <p className="mt-3 text-[color:var(--text-2)] leading-relaxed max-w-xl">Postmoro's activity view shows publishing status across every network, the next drafts waiting in the queue, and how each workspace is spending its plan — all without leaving the app.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-4">
            {/* LEFT — publishing status by platform */}
            <GlowPanel variant="raised" className="p-5" aria-hidden="true">
              <div className="flex items-center justify-between">
                <div className="text-sm text-[color:var(--text-2)] inline-flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-[color:var(--brand-primary)]" />
                  Publishing by platform · demo
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Last 7 days</span>
              </div>
              <div className="mt-5 space-y-3">
                {[
                  { key: "facebook",  count: 4, pct: 76, spark: [1,2,1,2,3,2,4] },
                  { key: "instagram", count: 3, pct: 62, spark: [1,1,2,2,3,3,3] },
                  { key: "linkedin",  count: 3, pct: 58, spark: [0,1,1,2,2,3,3] },
                  { key: "x",         count: 2, pct: 34, spark: [0,1,0,1,1,2,2] },
                ].map((row) => (
                  <div key={row.key} className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <PlatformBadge variant="postmoro" platform={row.key} size="sm" />
                        <span className="text-[11px] text-[color:var(--text-3)]">{row.count} published</span>
                      </div>
                      <Sparkline points={row.spark} width={110} height={26} />
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-[color:rgba(255,255,255,0.06)] overflow-hidden">
                      <div className="h-full rounded-full" style={{
                        width: row.pct + "%",
                        background: `linear-gradient(90deg, #1BA89F, #22D3EE)`,
                        boxShadow: "0 0 10px rgba(27,168,159,0.55)",
                      }} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[10px] text-[color:var(--text-3)]">Bars &amp; sparklines above are static UI-demo placeholders, not real customer data.</p>
            </GlowPanel>

            {/* RIGHT — scheduled queue + plan usage stacked */}
            <div className="grid grid-cols-1 gap-4">
              <GlowPanel variant="raised" className="p-5" aria-hidden="true">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-[color:var(--text-2)] inline-flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[color:var(--brand-primary)]" />
                    Scheduled queue · demo
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Next 48h</span>
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    { when: "Thu 08:30", plat: "instagram", label: "Taco Tuesday — teaser reel",   state: "queued" },
                    { when: "Thu 12:00", plat: "facebook",  label: "Late-night service — kickoff", state: "queued" },
                    { when: "Fri 09:15", plat: "linkedin",  label: "Founder note · truck story",   state: "review" },
                    { when: "Fri 17:00", plat: "x",         label: "Weekend menu reminder",         state: "queued" },
                  ].map((q, i) => (
                    <div key={i} className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 flex items-center gap-3">
                      <div className="text-[10px] tabular-nums uppercase tracking-wider text-[color:var(--text-3)] w-16 shrink-0">{q.when}</div>
                      <PlatformBadge variant="postmoro" platform={q.plat} size="sm" />
                      <div className="text-xs text-[color:var(--text-1)] truncate flex-1 min-w-0">{q.label}</div>
                      <span className={
                        "text-[10px] px-2 py-0.5 rounded-full border shrink-0 " +
                        (q.state === "queued"
                          ? "border-[color:rgba(27,168,159,0.4)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]"
                          : "border-[color:rgba(255,190,110,0.4)] bg-[color:rgba(255,190,110,0.10)] text-[color:rgb(255,190,110)]")
                      }>
                        {q.state === "queued" ? "Queued" : "In review"}
                      </span>
                    </div>
                  ))}
                </div>
              </GlowPanel>

              <GlowPanel variant="raised" className="p-5" aria-hidden="true">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-[color:var(--text-2)] inline-flex items-center gap-2">
                    <Layers className="h-4 w-4 text-[color:var(--brand-primary)]" />
                    Workspace usage · demo
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Business plan</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <StatRing value={124} max={200} size={52} label="124 / 200" sub="AI generations" />
                  <StatRing value={38}  max={60}  size={52} label="38 / 60"   sub="Published posts" color="#22D3EE" />
                </div>
                <div className="mt-4">
                  <ProgressRail value={62} max={100} />
                </div>
                <p className="mt-3 text-[10px] text-[color:var(--text-3)]">Plan-usage figures above are UI-demo placeholders.</p>
              </GlowPanel>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------- 5A. Roadmap -------------------- */}
      <section className="relative py-20" data-testid="landing-roadmap">
        <SectionBackdrop />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <EyebrowTag>Roadmap</EyebrowTag>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Live Today — And What's Next</h2>
            <p className="mt-3 text-[color:var(--text-2)] leading-relaxed">
              Everything below with a teal check ships in every workspace today. Amber sparkles are on the near-term roadmap — we'll announce each one on <Link to="/whats-new" className="text-[color:var(--brand-primary)] hover:underline">What's New</Link>.
            </p>
          </div>

          {/* LIVE NOW — horizontal timeline (desktop) / stepped list (mobile) */}
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2 w-2 rounded-full bg-[color:var(--brand-primary)] shadow-[0_0_10px_rgba(27,168,159,0.85)]" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--brand-primary)]">Live now · in every plan</span>
            </div>
            <div className="relative">
              {/* rail (desktop only) */}
              <div aria-hidden="true"
                   className="hidden lg:block absolute left-0 right-0 top-[26px] h-[2px]"
                   style={{ background: "linear-gradient(90deg, rgba(27,168,159,0.55), rgba(92,214,192,0.45), rgba(47,143,229,0.35), rgba(27,168,159,0.10))" }} />
              <ol className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 lg:gap-2 relative">
                {[
                  { label: "AI Post Generation", icon: Wand2 },
                  { label: "Scheduling",          icon: CalendarDays },
                  { label: "Autopilot",           icon: Plane },
                  { label: "Brand Learning",      icon: BookOpen },
                  { label: "Analytics",           icon: BarChart3 },
                  { label: "Multi-Business",      icon: Layers },
                  { label: "Teams",               icon: Users },
                  { label: "Media Library",       icon: Sparkles },
                ].map((step, i) => (
                  <li key={step.label} className="relative flex flex-col items-center text-center">
                    <div className="relative z-10 h-[54px] w-[54px] rounded-full flex items-center justify-center border-2 border-[color:rgba(27,168,159,0.55)] bg-[color:var(--surface-1)] shadow-[0_10px_28px_-14px_rgba(27,168,159,0.75)]">
                      <step.icon className="h-5 w-5 text-[color:var(--brand-primary)]" />
                      <span aria-hidden="true"
                            className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] text-[9px] font-bold flex items-center justify-center">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                    </div>
                    <div className="mt-3 text-[11px] sm:text-xs font-medium text-[color:var(--text-1)] leading-tight max-w-[110px]">{step.label}</div>
                    <div className="text-[9px] uppercase tracking-wider text-[color:var(--text-3)] mt-0.5">Step {i + 1}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* COMING SOON — grid pulled from real coming-soon feature list */}
          <div className="mt-14">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-2 w-2 rounded-full bg-[color:rgb(255,190,110)] shadow-[0_0_10px_rgba(255,190,110,0.85)]" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[color:rgb(255,190,110)]">Coming soon</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {COMING_SOON_FEATURES.map((f) => (
                <GlowPanel key={f.id} variant="raised" className="p-4 border-[color:rgba(255,190,110,0.28)] flex gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-md flex items-center justify-center bg-[color:rgba(255,190,110,0.10)] text-[color:rgb(255,190,110)]">
                    <f.icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-sm font-semibold text-[color:var(--text-1)] truncate">{f.name}</div>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[color:rgba(255,190,110,0.14)] text-[color:rgb(255,190,110)] border border-[color:rgba(255,190,110,0.35)] shrink-0">
                        {f.planLabel}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] text-[color:var(--text-2)] leading-relaxed">{f.short}</p>
                  </div>
                </GlowPanel>
              ))}
            </div>
            <p className="mt-4 text-xs text-[color:var(--text-3)]">
              None of the Coming Soon items above are live yet. Postmoro will announce each release on <Link to="/whats-new" className="text-[color:var(--brand-primary)] hover:underline">What's New</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* -------------------- 6. Pricing -------------------- */}
      <section className="relative py-16" data-testid="landing-pricing">
        <SectionBackdrop />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center">
            <EyebrowTag>Pricing</EyebrowTag>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Simple Plans, Real Limits</h2>
            <p className="mt-4 text-[color:var(--text-2)] max-w-2xl mx-auto">Every plan includes AI generation, cross-platform publishing, and Autopilot. Higher tiers raise the monthly limits and unlock Multi-Business, Teams, and Business media.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((t) => (
              <GlowPanel
                key={t.id}
                variant={t.highlight ? "featured" : "raised"}
                className="p-5 flex flex-col"
                data-testid={`landing-plan-${t.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg font-semibold">{t.name}</div>
                  {t.highlight && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">Popular</span>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-2xl font-semibold">${fmtPrice(t.price)}</span>
                  <span className="text-xs text-[color:var(--text-3)]">/ {t.period}</span>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm text-[color:var(--text-2)] flex-1">
                  {(PLAN_HIGHLIGHTS[t.id] || t.features || []).map((f) => (
                    <li key={f} className="inline-flex items-start gap-2">
                      <Check className="h-4 w-4 text-[color:var(--brand-primary)] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {(COMING_SOON_BY_PLAN[t.id] || []).length > 0 && (
                    <>
                      <li className="pt-3 mt-2 border-t border-[color:var(--border-subtle)]" aria-hidden="true" />
                      {(COMING_SOON_BY_PLAN[t.id] || []).map((f) => (
                        <li key={f} className="inline-flex items-start gap-2" data-testid={`landing-plan-${t.id}-coming-soon-row`}>
                          <Sparkles className="h-4 w-4 text-[color:rgb(255,190,110)] shrink-0 mt-0.5" aria-hidden="true" />
                          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                            <span>{f.replace(/\s*—\s*Coming Soon\s*$/i, "")}</span>
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[color:rgba(255,190,110,0.12)] text-[color:rgb(255,190,110)] border border-[color:rgba(255,190,110,0.32)]">
                              Coming soon
                            </span>
                          </span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
                <Link to="/login" className="mt-5">
                  <Button
                    className={`w-full font-semibold ${t.highlight ? "bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]" : "border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]"}`}
                    data-testid={`landing-plan-cta-${t.id}`}
                  >
                    {t.cta} <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </GlowPanel>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Link to="/pricing" className="text-sm text-[color:var(--brand-primary)] hover:underline">See full pricing comparison <ArrowRight className="inline h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </section>

      {/* -------------------- 7. Final CTA -------------------- */}
      <section className="relative py-20" data-testid="landing-final-cta">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[380px] w-[900px] max-w-full rounded-full opacity-[0.25]" style={{ background: "radial-gradient(ellipse at center, rgba(27,168,159,0.55), rgba(27,168,159,0) 65%)", filter: "blur(40px)" }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <EyebrowTag><Zap className="h-3 w-3" /> Try free</EyebrowTag>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight">Give Your Social Media Its Calmest Week</h2>
          <p className="mt-4 text-[color:var(--text-2)] max-w-xl mx-auto">Start free, connect a network, generate your first post. Postmoro handles the rest.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold px-6" data-testid="landing-cta-final">
                Get Started Free <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/features">
              <Button size="lg" variant="outline" className="border-[color:var(--border-subtle)] text-[color:var(--text-1)] px-6" data-testid="landing-cta-final-features">
                See all features
              </Button>
            </Link>
          </div>
          <p className="mt-3 text-xs text-[color:var(--text-3)]">No credit card required. Cancel any time.</p>
        </div>
      </section>

      {/* -------------------- Footer (preserved from P1) -------------------- */}
      <footer className="border-t border-[color:var(--border-subtle)] py-8 text-center text-xs text-[color:var(--text-3)]" data-testid="landing-footer">
        <div>© {new Date().getFullYear()} Postmoro LLC</div>
        <div className="mt-2 flex items-center justify-center gap-4 flex-wrap">
          <Link to="/features" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-features">Features</Link>
          <Link to="/pricing" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-pricing">Pricing</Link>
          <Link to="/about" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-about">About</Link>
          <Link to="/contact" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-contact">Contact</Link>
          <Link to="/whats-new" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-whats-new">What&apos;s New</Link>
          <Link to="/affiliates" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-affiliates">Affiliates</Link>
          <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-privacy">Privacy</Link>
          <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-terms">Terms</Link>
          <Link to="/data-deletion" className="hover:text-[color:var(--text-1)]" data-testid="landing-footer-data">Data Deletion</Link>
        </div>
      </footer>
    </div>
  );
}
